import Konva from 'konva';
import { tr } from '../services/i18n.js';

// Vue tactique. POSITION PRODUIT (retour utilisateur) : tant qu'on n'a pas de vrai
// tracking joueurs/ballon, les coordonnées renvoyées par l'IA peuvent être de la
// fiction. On REFUSE donc d'afficher par défaut un schéma terrain qui prétendrait
// reproduire la phase : ce serait trompeur. Par défaut on rend un panneau sobre
// « Lecture tactique » qui renvoie à l'analyse textuelle (la vraie source fiable).
// Le schéma terrain n'est dessiné QUE si les données passent un garde-fou TRÈS
// strict ET portent un signal de fiabilité explicite (cf. pitchReliability). En
// l'état des données IA actuelles, ce garde-fou n'est pas franchi : le terrain
// reste masqué, et c'est volontaire.

const TEAM = {
  A: { fill: '#2f80ed', halo: 'rgba(47,128,237,0.18)', name: () => tr('Équipe A', 'Team A') },
  B: { fill: '#eb5757', halo: 'rgba(235,87,87,0.18)', name: () => tr('Équipe B', 'Team B') }
};

const ZONE_KINDS = {
  space: { fill: 'rgba(39,174,96,0.16)', stroke: '#27ae60', label: () => tr('Espace', 'Space') },
  halfspace: { fill: 'rgba(242,201,76,0.16)', stroke: '#f2c94c', label: () => tr('Demi-espace', 'Half-space') },
  channel: { fill: 'rgba(45,156,219,0.16)', stroke: '#2d9cdb', label: () => tr('Couloir', 'Channel') },
  danger: { fill: 'rgba(235,87,87,0.18)', stroke: '#eb5757', label: () => tr('Zone de danger', 'Danger zone') },
  pressing: { fill: 'rgba(242,153,74,0.18)', stroke: '#f2994a', label: 'Pressing' }
};

// Clamp neutre 0..100 ; renvoie null si la valeur n'est pas exploitable.
function pct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(100, n));
}

function teamColor(team) {
  return TEAM[team] || { fill: '#95a5a6', halo: 'rgba(149,165,166,0.18)', name: () => tr('Équipe', 'Team') };
}

function dist(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function renderPitchCanvas(container, analysis = {}) {
  // Garde-fou central : par défaut on n'affiche PAS de schéma terrain reconstitué.
  // On ne dessine le terrain que si les données sont jugées fiables (signal
  // explicite + cohérence stricte). Sinon, panneau sobre + renvoi à l'analyse texte.
  if (!pitchReliability(analysis).reliable) {
    renderTacticalPanel(container, analysis);
    return;
  }

  const wrapper = document.createElement('div');
  wrapper.className = 'glass-card';
  wrapper.innerHTML = `
    <div style="display:flex;align-items:baseline;gap:8px;margin-bottom:10px;flex-wrap:wrap;">
      <p style="font-weight:600;margin:0;">🗺️ ${tr('Vue tactique', 'Tactical view')}</p>
      <span style="font-size:12px;color:var(--text-muted,#90a4ae);">${tr('structure · soutien · espaces', 'structure · support · spaces')}</span>
    </div>
    <div id="konva-stage" style="width:100%;aspect-ratio:68/105;background:#1f7a43;border-radius:10px;overflow:hidden;"></div>
    <div id="pitch-legend" style="display:flex;flex-wrap:wrap;gap:8px 14px;margin-top:10px;font-size:11px;color:var(--text-muted,#90a4ae);"></div>
    <p id="pitch-note" style="display:none;font-size:11px;line-height:1.4;color:var(--text-muted,#90a4ae);margin:8px 0 0;"></p>
  `;
  container.appendChild(wrapper);

  const stageContainer = wrapper.querySelector('#konva-stage');
  const width = stageContainer.clientWidth || 320;
  const height = (width * 105) / 68;
  stageContainer.style.height = height + 'px';

  const stage = new Konva.Stage({ container: stageContainer, width, height });
  const layer = new Konva.Layer();
  stage.add(layer);

  const ctx = {
    layer,
    width,
    height,
    toX: (x) => (x / 100) * width,
    toY: (y) => (y / 100) * height,
    scale: width / 320 // facteur d'échelle pour tailles relatives
  };

  // Données nettoyées (clamp + filtrage des entrées inexploitables).
  const players = (Array.isArray(analysis.players_positions) ? analysis.players_positions : [])
    .map((p) => ({
      team: p.team === 'A' || p.team === 'B' ? p.team : undefined,
      role: typeof p.role === 'string' ? p.role : '',
      x: pct(p.x),
      y: pct(p.y),
      mx: pct(p.movement_x),
      my: pct(p.movement_y)
    }))
    .filter((p) => p.x !== null && p.y !== null)
    .slice(0, 14);

  // Flèches : filtrées et plafonnées aussi côté client (défense en profondeur pour
  // les anciennes analyses non re-normalisées). On écarte les flèches dégénérées
  // ou invraisemblables (qui traverseraient tout le terrain) et on plafonne à 8.
  const arrows = (Array.isArray(analysis.arrows) ? analysis.arrows : [])
    .map((a) => ({
      from: { x: pct(a.from_x), y: pct(a.from_y) },
      to: { x: pct(a.to_x), y: pct(a.to_y) },
      type: typeof a.type === 'string' ? a.type : 'pass',
      label: typeof a.label === 'string' ? a.label : ''
    }))
    .filter((a) => a.from.x !== null && a.from.y !== null && a.to.x !== null && a.to.y !== null)
    .filter((a) => {
      const len = Math.hypot(a.to.x - a.from.x, a.to.y - a.from.y);
      return len >= 3 && len <= 75;
    })
    .slice(0, 8);

  const ball = analysis.ball_position && pct(analysis.ball_position.x) !== null && pct(analysis.ball_position.y) !== null
    ? { x: pct(analysis.ball_position.x), y: pct(analysis.ball_position.y) }
    : null;

  // Overlays optionnels fournis par l'IA (compatibles anciennes analyses), bornés
  // pour rester sobres et lisibles même sur d'anciennes données non re-normalisées.
  const aiLines = sanitizePointList(analysis.tactical_lines).slice(0, 4);
  const aiShapes = sanitizePointList(analysis.tactical_shapes).slice(0, 4);
  const aiZones = sanitizeZones(analysis.highlight_zones).slice(0, 3);

  const legend = new Set();

  drawPitch(ctx);
  drawLaneGuides(ctx);

  // PRUDENCE (retour utilisateur) : on ne DESSINE QUE des éléments justifiés par
  // l'IA. Plus aucune structure/soutien inventé par heuristique de proximité (qui
  // produisait des « flèches dans tous les sens » non conformes à la phase). En
  // l'absence d'overlays IA, on affiche un terrain sobre avec joueurs + ballon et
  // un avertissement honnête, plutôt qu'un faux schéma.

  // Zones clés : uniquement celles fournies par l'IA.
  aiZones.forEach((z) => { drawZone(ctx, z); legend.add('zone'); });

  // Structure d'équipe : uniquement les lignes fournies par l'IA.
  if (aiLines.length) {
    aiLines.forEach((l) => { drawPolyline(ctx, l.points, l.team); legend.add('structure'); });
  }

  // Réseau de soutien (triangles/angles) : uniquement les formes fournies par l'IA.
  if (aiShapes.length) {
    aiShapes.forEach((s) => { drawSupportShape(ctx, s.points, s.team); legend.add('soutien'); });
  }

  // Flèches d'action (passe / course / tir) déjà filtrées et plafonnées côté
  // Worker. On les dessine telles quelles, avec leur libellé.
  arrows.forEach((a) => { drawActionArrow(ctx, a); legend.add('action'); });

  // Déplacements joueurs (flèches pointillées) + animation discrète. Seulement si
  // un vecteur de déplacement net est fourni par l'IA.
  players.forEach((p, i) => {
    if (p.mx !== null && p.my !== null && dist(p, { x: p.mx, y: p.my }) > 1.5) {
      drawMovement(ctx, p, i);
      legend.add('deplacement');
    }
  });

  // Halo + rayon de pression autour du porteur présumé (joueur le plus proche
  // du ballon, seulement s'il est nettement le plus proche).
  const carrier = ball ? nearestPlayer(players, ball) : null;
  if (carrier && carrier.d < 9) {
    drawBallContext(ctx, carrier.player);
    legend.add('porteur');
  }

  // Joueurs au-dessus des overlays pour rester lisibles.
  players.forEach((p) => drawPlayer(ctx, p));

  if (ball) { drawBall(ctx, ball); legend.add('ballon'); }

  layer.draw();
  renderLegend(wrapper.querySelector('#pitch-legend'), legend);

  // Honnêteté de la vue : si l'IA n'a fourni aucun schéma exploitable (ni lignes,
  // ni soutiens, ni zones, ni flèches), on l'indique explicitement au lieu de
  // laisser croire à un schéma exact reconstitué.
  const hasSchema = aiLines.length || aiShapes.length || aiZones.length || arrows.length;
  const note = wrapper.querySelector('#pitch-note');
  if (note && !hasSchema) {
    note.textContent = players.length
      ? tr("Vue indicative : positions approximatives issues de l'analyse. L'IA n'a pas fourni de schéma détaillé (lignes, soutiens, flèches) pour cette action.", 'Indicative view: approximate positions from the analysis. The AI did not provide a detailed board (lines, support, arrows) for this action.')
      : tr("L'IA n'a pas fourni de positions fiables pour cette action : aucun schéma n'est affiché pour éviter de représenter une situation inexacte.", 'The AI did not provide reliable positions for this action: no board is shown to avoid representing an inaccurate situation.');
    note.style.display = 'block';
  }

  // Responsive : on conserve l'aspect ratio et on met à l'échelle le rendu.
  const ro = new ResizeObserver(() => {
    const w = stageContainer.clientWidth;
    if (!w) return;
    const h = (w * 105) / 68;
    stage.width(w);
    stage.height(h);
    stage.scale({ x: w / width, y: h / height });
    stage.batchDraw();
  });
  ro.observe(stageContainer);
}

// --- Terrain ---

function drawPitch(ctx) {
  const { layer, width: w, height: h } = ctx;
  const line = 'rgba(255,255,255,0.75)';
  const lw = 1.5 * ctx.scale;

  // Bandes de tonte alternées (sobre, lisibilité « plateau »).
  const stripes = 8;
  for (let i = 0; i < stripes; i++) {
    layer.add(new Konva.Rect({
      x: 0, y: (h / stripes) * i, width: w, height: h / stripes,
      fill: i % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.04)'
    }));
  }

  layer.add(new Konva.Rect({ x: 0, y: 0, width: w, height: h, stroke: line, strokeWidth: lw }));
  layer.add(new Konva.Line({ points: [0, h / 2, w, h / 2], stroke: line, strokeWidth: lw }));
  layer.add(new Konva.Circle({ x: w / 2, y: h / 2, radius: w * 0.13, stroke: line, strokeWidth: lw }));
  layer.add(new Konva.Circle({ x: w / 2, y: h / 2, radius: 2.5 * ctx.scale, fill: line }));

  // Surfaces de réparation + surfaces de but.
  const paW = w * 0.44, paH = h * 0.15;
  const gaW = w * 0.22, gaH = h * 0.06;
  [0, h].forEach((edge) => {
    const top = edge === 0;
    layer.add(new Konva.Rect({ x: (w - paW) / 2, y: top ? 0 : h - paH, width: paW, height: paH, stroke: line, strokeWidth: lw }));
    layer.add(new Konva.Rect({ x: (w - gaW) / 2, y: top ? 0 : h - gaH, width: gaW, height: gaH, stroke: line, strokeWidth: lw }));
    layer.add(new Konva.Circle({ x: w / 2, y: top ? paH : h - paH, radius: 2 * ctx.scale, fill: line }));
  });

  // Cages.
  const gW = w * 0.13;
  layer.add(new Konva.Rect({ x: (w - gW) / 2, y: -3, width: gW, height: 5, fill: line, opacity: 0.6 }));
  layer.add(new Konva.Rect({ x: (w - gW) / 2, y: h - 2, width: gW, height: 5, fill: line, opacity: 0.6 }));
}

// Repères de couloirs / demi-espaces (5 corridors), très discrets.
function drawLaneGuides(ctx) {
  const { layer, width: w, height: h } = ctx;
  [20, 40, 60, 80].forEach((x) => {
    layer.add(new Konva.Line({
      points: [ctx.toX(x), 0, ctx.toX(x), h],
      stroke: 'rgba(255,255,255,0.06)', strokeWidth: 1, dash: [4, 6]
    }));
  });
}

// --- Overlays tactiques ---

function drawZone(ctx, z) {
  const k = ZONE_KINDS[z.kind] || ZONE_KINDS.space;
  const x = ctx.toX(z.x - z.width / 2);
  const y = ctx.toY(z.y - z.height / 2);
  ctx.layer.add(new Konva.Rect({
    x, y, width: ctx.toX(z.width), height: ctx.toY(z.height),
    fill: k.fill, stroke: k.stroke, strokeWidth: 1, dash: [5, 4], cornerRadius: 6
  }));
  const text = z.label || (typeof k.label === 'function' ? k.label() : k.label);
  if (text) {
    ctx.layer.add(new Konva.Text({
      x: x + 4, y: y + 4, text, fontSize: 9 * ctx.scale,
      fill: k.stroke, fontFamily: 'Inter', fontStyle: '600'
    }));
  }
}

function drawPolyline(ctx, pts, team, withDots) {
  if (!pts || pts.length < 2) return;
  const color = teamColor(team).fill;
  const flat = [];
  pts.forEach((p) => { flat.push(ctx.toX(p.x), ctx.toY(p.y)); });
  ctx.layer.add(new Konva.Line({
    points: flat, stroke: color, strokeWidth: 1.6 * ctx.scale,
    opacity: 0.45, lineJoin: 'round', dash: [7, 5]
  }));
  if (withDots) {
    pts.forEach((p) => ctx.layer.add(new Konva.Circle({
      x: ctx.toX(p.x), y: ctx.toY(p.y), radius: 2 * ctx.scale, fill: color, opacity: 0.5
    })));
  }
}

function drawSupportShape(ctx, pts, team) {
  if (!pts || pts.length < 2) return;
  const color = teamColor(team).fill;
  const flat = [];
  pts.forEach((p) => { flat.push(ctx.toX(p.x), ctx.toY(p.y)); });
  const closed = pts.length >= 3;
  ctx.layer.add(new Konva.Line({
    points: flat, stroke: color, strokeWidth: 1.3 * ctx.scale, opacity: 0.4,
    closed, fill: closed ? teamColor(team).halo : undefined, lineJoin: 'round'
  }));
}

function drawActionArrow(ctx, a) {
  const isShot = a.type === 'shot';
  const isRun = a.type === 'run';
  const color = isShot ? '#f2994a' : isRun ? '#27ae60' : '#f2c94c';
  const dash = isShot ? [] : isRun ? [4, 4] : [8, 5];
  ctx.layer.add(new Konva.Arrow({
    points: [ctx.toX(a.from.x), ctx.toY(a.from.y), ctx.toX(a.to.x), ctx.toY(a.to.y)],
    pointerLength: 8 * ctx.scale, pointerWidth: 6 * ctx.scale,
    stroke: color, fill: color, strokeWidth: (isShot ? 3 : 2) * ctx.scale, dash
  }));
  const label = a.label || (isShot ? tr('Tir', 'Shot') : isRun ? tr('Course', 'Run') : tr('Passe', 'Pass'));
  if (label) {
    const mx = ctx.toX((a.from.x + a.to.x) / 2);
    const my = ctx.toY((a.from.y + a.to.y) / 2);
    ctx.layer.add(new Konva.Text({
      x: mx + 4, y: my - 10 * ctx.scale, text: label, fontSize: 9 * ctx.scale,
      fill: color, fontFamily: 'Inter', fontStyle: '600'
    }));
  }
}

function drawMovement(ctx, p, i) {
  const color = teamColor(p.team).fill;
  const fx = ctx.toX(p.x), fy = ctx.toY(p.y);
  const tx = ctx.toX(p.mx), ty = ctx.toY(p.my);
  ctx.layer.add(new Konva.Arrow({
    points: [fx, fy, tx, ty], pointerLength: 6 * ctx.scale, pointerWidth: 4 * ctx.scale,
    stroke: color, strokeWidth: 1.6 * ctx.scale, dash: [4, 4], opacity: 0.65
  }));
  const dot = new Konva.Circle({ x: fx, y: fy, radius: 2.5 * ctx.scale, fill: color, opacity: 0.55 });
  ctx.layer.add(dot);
  const tween = new Konva.Tween({
    node: dot, duration: 1.5, x: tx, y: ty, easing: Konva.Easings.EaseInOut,
    onFinish: () => setTimeout(() => { dot.x(fx); dot.y(fy); tween.play(); }, 600)
  });
  setTimeout(() => tween.play(), i * 200);
}

function drawBallContext(ctx, p) {
  // Halo porteur + rayon de pression indicatif.
  ctx.layer.add(new Konva.Circle({
    x: ctx.toX(p.x), y: ctx.toY(p.y), radius: ctx.width * 0.045,
    stroke: '#f2c94c', strokeWidth: 2 * ctx.scale, opacity: 0.85
  }));
  ctx.layer.add(new Konva.Circle({
    x: ctx.toX(p.x), y: ctx.toY(p.y), radius: ctx.width * 0.11,
    stroke: '#f2c94c', strokeWidth: 1 * ctx.scale, dash: [4, 5], opacity: 0.4
  }));
}

function drawPlayer(ctx, p) {
  const tc = teamColor(p.team);
  const x = ctx.toX(p.x), y = ctx.toY(p.y);
  const r = ctx.width * 0.026;
  // Halo léger pour détacher du fond.
  ctx.layer.add(new Konva.Circle({ x, y, radius: r * 1.5, fill: tc.halo }));
  ctx.layer.add(new Konva.Circle({ x, y, radius: r, fill: tc.fill, stroke: '#fff', strokeWidth: 1.6 * ctx.scale }));
  const initial = roleInitial(p.role);
  if (initial) {
    ctx.layer.add(new Konva.Text({
      x: x - r, y: y - r * 0.55, width: r * 2, align: 'center',
      text: initial, fontSize: r * 1.05, fill: '#fff', fontFamily: 'Inter', fontStyle: '700'
    }));
  }
}

function drawBall(ctx, ball) {
  ctx.layer.add(new Konva.Circle({
    x: ctx.toX(ball.x), y: ctx.toY(ball.y), radius: ctx.width * 0.019,
    fill: '#fff', stroke: '#1b1b1b', strokeWidth: 1.4 * ctx.scale
  }));
}

// --- Heuristiques (réduites au strict nécessaire) ---
// Plus aucune reconstruction de structure/soutien par proximité : ces heuristiques
// dessinaient des lignes « dans tous les sens » non conformes à la phase de jeu.
// On ne conserve que la détection du porteur présumé (halo autour du ballon).

function nearestPlayer(players, ball) {
  let best = null;
  players.forEach((p) => {
    const d = dist(p, ball);
    if (!best || d < best.d) best = { player: p, d };
  });
  return best;
}

function roleInitial(role) {
  if (!role) return '';
  const r = role.toLowerCase();
  if (r.startsWith('gard')) return 'G';
  if (r.startsWith('def')) return 'D';
  if (r.startsWith('mil')) return 'M';
  if (r.startsWith('att')) return 'A';
  return role.charAt(0).toUpperCase();
}

// --- Normalisation des overlays optionnels côté client (défense en profondeur) ---

function sanitizePointList(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => ({
      team: item && (item.team === 'A' || item.team === 'B') ? item.team : undefined,
      points: Array.isArray(item && item.points)
        ? item.points
            .map((p) => ({ x: pct(p && p.x), y: pct(p && p.y) }))
            .filter((p) => p.x !== null && p.y !== null)
        : []
    }))
    .filter((item) => item.points.length >= 2);
}

function sanitizeZones(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((z) => ({
      x: pct(z && z.x),
      y: pct(z && z.y),
      width: pct(z && z.width),
      height: pct(z && z.height),
      kind: z && ZONE_KINDS[z.kind] ? z.kind : 'space',
      label: z && typeof z.label === 'string' ? z.label.slice(0, 40) : ''
    }))
    .filter((z) => z.x !== null && z.y !== null && z.width && z.height);
}

// --- Légende dynamique (libellés tactiques uniquement) ---

const LEGEND_ITEMS = {
  structure: { swatch: '#2f80ed', text: () => tr('Structure / lignes', 'Structure / lines') },
  soutien: { swatch: '#27ae60', text: () => tr('Réseau de soutien', 'Support network') },
  action: { swatch: '#f2c94c', text: () => tr('Action (passe / tir / course)', 'Action (pass / shot / run)') },
  deplacement: { swatch: '#9b59b6', text: () => tr('Déplacement', 'Movement') },
  porteur: { swatch: '#f2c94c', text: () => tr('Porteur & pression', 'Carrier & pressure') },
  zone: { swatch: '#2d9cdb', text: () => tr('Zone clé', 'Key zone') },
  ballon: { swatch: '#ffffff', text: () => tr('Ballon', 'Ball') }
};

function renderLegend(el, keys) {
  if (!el) return;
  const order = ['structure', 'soutien', 'action', 'deplacement', 'porteur', 'zone', 'ballon'];
  const html = order
    .filter((k) => keys.has(k))
    .map((k) => {
      const it = LEGEND_ITEMS[k];
      return `<span style="display:inline-flex;align-items:center;gap:5px;">
        <span style="width:10px;height:10px;border-radius:50%;background:${it.swatch};border:1px solid rgba(0,0,0,0.2);"></span>${typeof it.text === 'function' ? it.text() : it.text}
      </span>`;
    })
    .join('');
  el.innerHTML = html;
}

// --- Garde-fou de fiabilité du schéma terrain ---

// Décide si un schéma terrain peut être dessiné sans tromper l'utilisateur.
// Très strict par conception : sans vrai tracking joueurs/ballon, les coordonnées
// IA ne sont pas vérifiables. On exige donc un SIGNAL DE FIABILITÉ EXPLICITE
// (analysis.tracking_reliable === true), posé en amont uniquement par une vraie
// source de positions. Tant que ce signal n'existe pas dans les données (cas
// actuel), on renvoie reliable=false et le terrain reste masqué. Les contrôles de
// cohérence ci-dessous restent une seconde barrière pour le jour où ce signal
// sera posé : assez de positions cohérentes, des deux équipes, et un ballon situé
// dans l'enveloppe des joueurs (pas une position aberrante hors-jeu de données).
function pitchReliability(analysis = {}) {
  const players = (Array.isArray(analysis.players_positions) ? analysis.players_positions : [])
    .map((p) => ({ team: p.team, x: pct(p.x), y: pct(p.y) }))
    .filter((p) => p.x !== null && p.y !== null);

  // Signal explicite du Worker — on vérifie quand même la cohérence minimale.
  if (analysis.tracking_reliable === true && players.length >= 6) {
    const hasA = players.some((p) => p.team === 'A');
    const hasB = players.some((p) => p.team === 'B');
    if (hasA && hasB) return { reliable: true, reason: 'ok' };
  }

  // Fallback : assez de joueurs identifiés, des deux équipes.
  if (players.length < 6) return { reliable: false, reason: 'too_few_players' };
  const hasA = players.some((p) => p.team === 'A');
  const hasB = players.some((p) => p.team === 'B');
  if (!hasA || !hasB) return { reliable: false, reason: 'one_team_only' };

  // Ballon, s'il est fourni, doit rester dans l'enveloppe plausible des joueurs.
  const ball = analysis.ball_position && pct(analysis.ball_position.x) !== null && pct(analysis.ball_position.y) !== null
    ? { x: pct(analysis.ball_position.x), y: pct(analysis.ball_position.y) }
    : null;
  if (ball) {
    const near = players.some((p) => dist(p, ball) < 18);
    if (!near) return { reliable: false, reason: 'ball_outlier' };
  }

  return { reliable: true, reason: 'ok' };
}

// Panneau sobre affiché à la place du schéma terrain quand les données ne sont
// pas fiables. Honnête et minimal : il assume qu'aucune carte n'est montrée et
// renvoie explicitement à l'analyse textuelle, qui reste la source principale.
function renderTacticalPanel(container, analysis = {}) {
  const players = (Array.isArray(analysis.players_positions) ? analysis.players_positions : [])
    .filter((p) => p && Number.isFinite(Number(p.x)) && Number.isFinite(Number(p.y))).length;

  const card = document.createElement('div');
  card.className = 'glass-card tactical-panel';
  card.innerHTML = `
    <div class="tactical-panel-head">
      <p class="tactical-panel-title">🗺️ ${tr('Lecture tactique', 'Tactical reading')}</p>
      <span class="tactical-panel-tag">${tr('Schéma terrain non affiché', 'Pitch board not shown')}</span>
    </div>
    <p class="tactical-panel-body">
      ${tr("Le schéma terrain n'est pas affiché pour cette séquence : faute d'un tracking joueurs/ballon assez robuste, des positions reconstruites représenteraient une situation potentiellement inexacte. L'analyse écrite ci-dessous reste la source fiable de la lecture tactique.", 'The pitch board is not shown for this sequence: without robust player/ball tracking, reconstructed positions could represent a potentially inaccurate situation. The written analysis below remains the reliable tactical source.')}
    </p>
    ${players ? `<p class="tactical-panel-foot">${tr(`${players} position${players > 1 ? 's' : ''} approximative${players > 1 ? 's' : ''} détectée${players > 1 ? 's' : ''} par l'IA, jugée${players > 1 ? 's' : ''} trop incertaine${players > 1 ? 's' : ''} pour un schéma — détaillées dans le texte.`, `${players} approximate position${players > 1 ? 's' : ''} detected by the AI, judged too uncertain for a board - detailed in the text.`)}</p>` : ''}
  `;
  container.appendChild(card);
}
